import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import GuestGuard from 'src/components/GuestGuard';
import AuthGuard from 'src/components/AuthGuard';

import NavRight from './layouts/SideLayout';
import EditorLayout from './views/editor/components/layouts/EditorLayout';
import WorkspaceLayout from './views/workspace/components/layouts/WorkspaceLayout';
import PricingGuard from './components/PricingGuard';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
    // component: lazy(() => import('src/components/SignUp'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/find-password',
    component: lazy(() => import('src/views/auth/FindPasswordView'))
    // component: lazy(() => import('src/components/SignUp'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },

  {
    exact: true,
    guard: AuthGuard,
    path: '/workspace/draft',
    layout: WorkspaceLayout,
    component: lazy(() => import('src/views/workspace/Draft'))
  },
  {
    exact: true,
    guard: AuthGuard,
    path: '/workspace/templates',
    layout: WorkspaceLayout,
    component: lazy(() => import('src/views/workspace/Templates'))
  },
  {
    exact: true,
    path: '/pricing',
    component: lazy(() => import('src/views/errors/PricingView'))
  },
  {
    exact: true,
    path: '/error',
    component: lazy(() => import('src/views/errors/NotSupportedView'))
  },
  {
    path: '/editor',
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/editor/auto-square-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/carousel-slide-modal/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-icon-modal/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      // {
      //   exact: true,
      //   path: '/editor/full-card-slide/:id?',
      //   layout: FullCardSlideLayout,
      //   component: lazy(() => import('src/views/editor/FullCardSlideView'))
      // },
      {
        exact: true,
        path: '/editor/basic-full-countdown/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/bottom-sheet-image/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/radius-modal-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/modal-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-modal-round/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/full-dual-image/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-survey/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-modal-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/bottom-sheet-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/nps-modal-survey/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/basic-full-image/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/radius-full-countdown/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-modal/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/multiple-choice-survey/:id?',
        // guard: PricingGuard,
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/modal-slider-survey/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-full-image/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-modal-image/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/auto-modal-slide/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/coupon-bottom-sheet/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/image-bottom-sheet/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/list-modal/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/alarm-modal/:id?',
        layout: NavRight,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/short-answer-survey/:id?',
        // guard: PricingGuard,
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/rating-answer-survey/:id?',
        // guard: PricingGuard,
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  // {
  //   path: '/catalog',
  //   guard: AuthGuard,
  //   routes: [
  //     {
  //       exact: true,
  //       path: '/catalog/modal-slide/:id?',
  //       layout: NavRight,
  //       // layout: ModalSlickContentsCatalog,
  //       component: lazy(() =>
  //         import('src/views/catalog/ModalSlickContentsView')
  //       )
  //     },
  //     {
  //       component: () => <Redirect to="/404" />
  //     }
  //   ]
  // },
  {
    path: '*',
    routes: [
      {
        exact: true,
        path: '/',
        component: lazy(() => import('src/views/main/MainView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
